import { TokenKey } from '../types/internal-enums';

/**
 * PKCE (RFC 7636, S256) for the code-flow app launch.
 *
 * The `code_verifier` is a secret kept in `localStorage` across the
 * app → IDMS → app round trip. localStorage — NOT sessionStorage — because the
 * round trip crosses origins and, under browser site-isolation, swaps renderer
 * processes; a per-tab sessionStorage writing made just before the outbound
 * redirect can fail to survive to the callback (observed as an empty verifier at
 * redeem). localStorage is durable and same-origin-shared, so the callback always
 * reads it back; it is cleared on redemption (single-use). Only the
 * `code_challenge` (its SHA-256 hash) travels on the outbound redirect URL. IDMS
 * bakes the challenge into the one-time code and verifies the presented verifier
 * at `POST /redeem-code`.
 */
export class PkceHelper {
	/**
	 * 43-char base64url verifier from 32 CSPRNG bytes — inside RFC 7636's 43–128
	 * range, unreserved charset (base64url ⊂ unreserved).
	 */
	static createVerifier(): string {
		const bytes: Uint8Array = new Uint8Array(32);
		crypto.getRandomValues(bytes);
		return PkceHelper.toBase64Url(bytes);
	}

	/** `BASE64URL(SHA256(verifier))` — the S256 challenge (RFC 7636 §4.2). */
	static async createChallenge(verifier: string): Promise<string> {
		const digest: ArrayBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
		return PkceHelper.toBase64Url(new Uint8Array(digest));
	}

	/**
	 * Generate a verifier, stash it for the pending `/redeem-code`, and build the
	 * IDMS login redirect URL carrying the S256 challenge. Returned (not applied)
	 * so the caller assigns `window.location.href` — keeps this unit testable
	 * without triggering navigation.
	 */
	static async buildLoginRedirectUrl(
		options: { frontendUrl: string; application: string },
		redirectPath: string
	): Promise<string> {
		const verifier: string = PkceHelper.createVerifier();
		const challenge: string = await PkceHelper.createChallenge(verifier);
		localStorage.setItem(TokenKey.pkceVerifier, verifier);

		return (
			`${options.frontendUrl}/apps/${options.application}` +
			`?redirect=true&redirect_path=${encodeURIComponent(redirectPath)}` +
			`&code_challenge=${challenge}&code_challenge_method=S256`
		);
	}

	/** The verifier stashed for the pending redemption (or `null`). */
	static readVerifier(): string | null {
		return localStorage.getItem(TokenKey.pkceVerifier);
	}

	static clearVerifier(): void {
		localStorage.removeItem(TokenKey.pkceVerifier);
	}

	private static toBase64Url(bytes: Uint8Array): string {
		let binary: string = '';
		for (const byte of bytes) {
			binary += String.fromCharCode(byte);
		}
		return btoa(binary)
			.replace(/\+/g, '-')
			.replace(/\//g, '_')
			.replace(/=+$/, '');
	}
}
